import { useEffect } from "react";
import { Button, Form, Upload, Image as AntImage } from "antd";
import type { GetProp, UploadFile, UploadProps } from "antd";
import { UploadOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form/Form";
import Input from "antd/es/input/Input";
import { isEmpty } from "lodash";

import "react-quill/dist/quill.snow.css";

const MAX_DATA_IMAGE_SIZE = 1;

type ProductProps = {
  form: FormInstance;
  formOptions: { brand: any; modal: any; tag: any };
};

export function BidProductForm(props: ProductProps) {
  useEffect(() => {
    props.form.resetFields();
  }, [props.form]);

  const markTravelLicenseImagePath = Form.useWatch("markTravelLicenseImagePath", props.form);
  const markAppraisalImagePath = Form.useWatch("markAppraisalImagePath", props.form);

  const getFile = (formItemKey: string) => {
    return props.form.getFieldValue(formItemKey) || [];
  };

  return (
    <Form className="product-form" form={props.form} initialValues={{}}>
      <Form.Item name="name" label="聯絡人" rules={[{ required: true, message: "請輸入聯絡人" }]}>
        <Input />
      </Form.Item>

      <Form.Item
        label="電話號碼"
        name="phone"
        rules={[
          { required: true, message: "請輸入電話" },
          {
            type: "string",
            message: "請輸入有效的電話號碼",
          },
          {
            validator: async (rule, value) => {
              if (value && !/^09\d{8}$/.test(value)) {
                throw new Error("請輸入有效的電話號碼: 09xxxxxxxx");
              }
            },
          },
        ]}
      >
        <Input allowClear />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "請輸入Email" },
          {
            type: "email",
            message: "請輸入有效的Email",
          },
        ]}
      >
        <Input allowClear />
      </Form.Item>

      <Form.Item
        label="底價"
        name="basePrice"
        rules={[
          { required: true, message: "請輸入底價" },
          {
            validator: async (rule, value) => {
              value = String(Number(value));

              if (value <= 0) {
                throw new Error("底價需大於1");
              } else if (/^[0-9]+\.[0-9]+$/.test(value)) {
                throw new Error("不允許小數");
              } else if (!/^\d{1,9}$/.test(value)) {
                throw new Error("位數需小於9位");
              }
            },
          },
        ]}
      >
        <Input type="number" onWheel={e => e.currentTarget.blur()} />
      </Form.Item>

      <Form.Item
        label="行照照片（遮蔽個資）"
        name="markTravelLicenseImagePath"
        valuePropName="markTravelLicenseImagePath"
        getValueFromEvent={() => getFile("markTravelLicenseImagePath")}
        extra="請自行將個資遮蔽再上傳"
        rules={[
          {
            required: true,
            message: "請選擇行照照片（遮蔽個資）",
          },
          () => ({
            async validator(_, value) {
              let element: File;
              value = value ?? [];

              if (value.length > MAX_DATA_IMAGE_SIZE) {
                return Promise.reject(new Error(`行照照片總數只能${MAX_DATA_IMAGE_SIZE}張`));
              }

              for (let index = 0; index < value.length; index++) {
                element = value[index];
                // const imageInfo: any = await loadImage(element);

                if (element instanceof File) {
                  if (element.size > 1048576) {
                    return Promise.reject(new Error(`檔案太大，需小於 1MB（${element.name}）`));
                  }
                } else if (!("url" in element)) {
                  return Promise.reject(new Error(`檔案讀取有誤`));
                }
              }

              return Promise.resolve();
            },
          }),
        ]}
      >
        <Upload
          accept=".jpg,.png"
          listType="picture-card"
          beforeUpload={async (file: File) => {
            const imageList = props.form.getFieldValue("markTravelLicenseImagePath") || [];
            props.form.setFieldValue("markTravelLicenseImagePath", imageList.concat(file));

            // 因為默認會有action 所以一定要 return false 不然會404
            return false;
          }}
          onPreview={onPreview}
          onRemove={(file: UploadFile) => {
            const imageList = props.form.getFieldValue("markTravelLicenseImagePath") || [];
            props.form.setFieldValue(
              "markTravelLicenseImagePath",
              imageList.filter((item: UploadFile) => item.uid !== file.uid)
            );
          }}
          defaultFileList={markTravelLicenseImagePath}
        >
          <Button icon={<UploadOutlined />}>選擇</Button>
        </Upload>
      </Form.Item>

      <Form.Item
        label="車輛鑑定書（遮蔽個資）"
        name="markAppraisalImagePath"
        valuePropName="markAppraisalImagePath"
        getValueFromEvent={() => getFile("markAppraisalImagePath")}
        extra="請自行將個資遮蔽再上傳"
        rules={[
          () => ({
            async validator(_, value) {
              let element: File;
              value = value ?? [];

              if (value.length > MAX_DATA_IMAGE_SIZE) {
                return Promise.reject(new Error(`車輛鑑定書只能${MAX_DATA_IMAGE_SIZE}張`));
              }

              for (let index = 0; index < value.length; index++) {
                element = value[index];
                // const imageInfo: any = await loadImage(element);

                if (element instanceof File) {
                  if (element.size > 1048576) {
                    return Promise.reject(new Error(`檔案太大，需小於 1MB（${element.name}）`));
                  }
                } else if (!("url" in element)) {
                  return Promise.reject(new Error(`檔案讀取有誤`));
                }
              }

              return Promise.resolve();
            },
          }),
        ]}
      >
        <Upload
          accept=".jpg,.png,.pdf"
          listType="picture-card"
          beforeUpload={async (file: File) => {
            const imageList = props.form.getFieldValue("markAppraisalImagePath") || [];
            props.form.setFieldValue("markAppraisalImagePath", imageList.concat(file));

            // 因為默認會有action 所以一定要 return false 不然會404
            return false;
          }}
          itemRender={(originNode, file: any, fileList, actions: any) =>
            file.url ? (
              originNode
            ) : (
              <div className="ant-upload-list-item">
                {file.name && (
                  <AntImage
                    src={file.thumbUrl}
                    fallback={
                      file.name.substring(file.name.length - 4) === ".pdf"
                        ? "https://cdn-icons-png.flaticon.com/512/201/201153.png"
                        : "https://cdn-icons-png.flaticon.com/512/44/44289.png"
                    }
                    preview={false}
                    alt={file.name}
                  />
                )}
                <div className="ant-upload-list-item-actions d-flex mt-1">
                  {!isEmpty(file.originFileObj) && (
                    <Button
                      style={{ background: "transparent" }}
                      shape="circle"
                      size="small"
                      icon={<EyeOutlined />}
                      type="link"
                      href={URL.createObjectURL(file.originFileObj)}
                      target="_blank"
                    />
                  )}
                  <Button
                    style={{ background: "transparent" }}
                    className="ant-btn ant-btn-text ant-btn-color-default ant-btn-variant-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-action"
                    shape="circle"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => actions.remove()}
                  />
                </div>
              </div>
            )
          }
          onPreview={onPreview}
          onRemove={(file: UploadFile) => {
            const imageList = props.form.getFieldValue("markAppraisalImagePath") || [];
            props.form.setFieldValue(
              "markAppraisalImagePath",
              imageList.filter((item: UploadFile) => item.uid !== file.uid)
            );
          }}
          defaultFileList={markAppraisalImagePath}
        >
          <Button icon={<UploadOutlined />}>選擇</Button>
        </Upload>
      </Form.Item>
    </Form>
  );
}

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const onPreview = async (file: UploadFile) => {
  let src = file.url as string;
  if (!src) {
    src = await new Promise(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(file.originFileObj as FileType);
      reader.onload = () => resolve(reader.result as string);
    });
  }

  if (src.substring(src.length - 4) === ".pdf") {
    window.open(src);
  } else {
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  }
};
